import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n, { type SupportedLanguage, RTL_LANGUAGES } from '@/lib/i18n';
import api from '@/lib/api';

interface I18nState {
  language: SupportedLanguage;
  dir: 'ltr' | 'rtl';
  setLanguage: (lang: SupportedLanguage) => Promise<void>;
}

function dirFor(lang: SupportedLanguage): 'ltr' | 'rtl' {
  return RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
}

/**
 * Single owner of the document direction/lang/font decision (T-458).
 * Derived from the language ONLY (the persisted value) - never from the
 * store's non-persisted `dir` default, which would clobber rehydration.
 */
export function applyDocumentLanguage(lang: SupportedLanguage): void {
  document.documentElement.dir = dirFor(lang);
  document.documentElement.lang = lang;
  if (lang === 'ur') {
    document.documentElement.classList.add('font-urdu');
  } else {
    document.documentElement.classList.remove('font-urdu');
  }
}

export const useI18nStore = create<I18nState>()(
  persist(
    (set) => ({
      language: 'en',
      dir: 'ltr',

      setLanguage: async (lang: SupportedLanguage) => {
        const dir = dirFor(lang);

        // Change i18next language
        await i18n.changeLanguage(lang);

        // Update DOM immediately
        applyDocumentLanguage(lang);

        set({ language: lang, dir });

        // Persist to backend (non-blocking — don't await, failure is acceptable)
        api.patch('/users/me/language', { language: lang }).catch(() => {
          // Ignore — preference already saved to localStorage
        });
      },
    }),
    {
      name: 'pos-lang',
      partialize: (state) => ({ language: state.language }),
      // `dir` is not persisted: derive it from the persisted language so the
      // in-memory store never holds the 'ltr' default alongside a saved 'ur'.
      merge: (persisted, current) => {
        const language = (persisted as Partial<I18nState> | undefined)?.language ?? current.language;
        return { ...current, language, dir: dirFor(language) };
      },
      onRehydrateStorage: () => (state) => {
        // Apply saved language to DOM after hydration (runs before first render)
        if (state?.language) {
          applyDocumentLanguage(state.language);
          void i18n.changeLanguage(state.language);
        }
      },
    }
  )
);
